import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useProgramCreation } from './ProgramCreationContext';
import { setBuildStateHydrating } from '../../program-collaboration/buildStateWriteGate';
import { API } from '../../../api';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchGroupDetails } from '../../../redux/slices/group-details/groupDetails';
import { useProgramContext } from '../../../hooks/useProgramContext';
import { useAimdHeartbeat } from '../../../pages/new-designs/tabs/aimd-v2/hooks/useAimdHeartbeat';

// Maps a persisted wizard buildState back to the sub-route the admin should
// resume at. Driven by the actual data present (not the numeric currentStep,
// which is ambiguous across sub-flows) so an AI course paused at "content
// generated" lands back on its Review step with the generated values shown.
function resolveResumeRoute(bs: any): string | null {
  if (!bs?.programType) return null;
  const type: string = bs.programType;
  const mode: string | null | undefined = bs.buildMode;

  if (type === 'mentorship') {
    const m = bs.mentorship || {};
    if ((m.mentors || []).length || (m.learningObjectives || []).some((o: string) => o?.trim?.())) {
      return 'mentorship/members';
    }
    if (m.name?.trim?.()) return 'mentorship/learning-objectives';
    return 'mentorship/program-name';
  }

  const typeBase = type === 'guided-masterclass' ? 'guided-masterclass' : 'masterclass';

  if (mode === 'manual') return `${typeBase}/manual`;

  if (mode === 'ai') {
    const ai = bs.ai || {};
    const modules = bs.modules || [];
    const hasContent = Boolean(ai.content) || modules.some((mod: any) => mod?.status === 'completed');
    const hasStructure = Boolean(ai.structure) || modules.length > 0;
    const hasDocs = (ai.documents || []).length > 0 || (ai.selectedDocIds || []).length > 0;
    const hasObjectives = (ai.objectives || []).some((o: string) => o?.trim?.());
    const hasPurpose = Boolean(ai.purpose?.description?.trim?.());

    if (hasContent || hasStructure) return `${typeBase}/ai/review`;
    if (hasDocs) return `${typeBase}/ai/documents`;
    if (hasObjectives) return `${typeBase}/ai/objectives`;
    if (hasPurpose) return `${typeBase}/ai/purpose`;
    return `${typeBase}/step-2`;
  }

  // Type chosen but no build mode yet → mode selection.
  return `${typeBase}/step-2`;
}

// The group's `model` is the source of truth for whether a program is a
// masterclass or a mentorship; the persisted wizard `buildState` is only a
// draft and can drift out of sync. We have seen cross-program contamination
// where a mentorship program's saved buildState carried
// programType: 'guided-masterclass' plus another program's masterclass.title —
// which made "Continue" resume into the masterclass module builder showing the
// wrong program name. Reconcile the two before hydrating/routing so the real
// model always wins.
function programTypeMatchesModel(
  programType: string | null | undefined,
  model: string | null | undefined
): boolean {
  if (!programType || !model) return true; // not enough info → don't interfere
  const family = programType === 'mentorship' ? 'mentorship' : 'masterclass';
  if (model === 'masterclass&mentorship') return true; // either type is valid
  if (model === 'mentorship') return family === 'mentorship';
  if (model === 'masterclass') return family === 'masterclass';
  return true; // unknown model value → don't interfere
}

// Returns a copy of the saved buildState corrected against the group's real
// identity, so the creation funnel resumes into the right builder showing the
// right name. Two independent corrections:
//   1. programType that contradicts the group `model` → coerce + drop the
//      conflicting per-type draft (the "wrong builder" symptom).
//   2. a stale/leaked draft title → the group `name` is the source of truth on
//      resume, so it wins. This is needed even when the type already matches:
//      a masterclass program can carry another program's masterclass.title.
// The title is only forced here on the resume read-path — never on save —
// because mid-edit the draft legitimately differs from the saved group name.
export function reconcileBuildStateWithModel(
  remote: any,
  model: string | null | undefined,
  groupName?: string | null
): any {
  if (!remote || typeof remote !== 'object') return remote;
  let next = remote;

  if (!programTypeMatchesModel(remote.programType, model)) {
    if (model === 'mentorship') {
      next = { ...next, programType: 'mentorship', masterclass: {}, guidedMasterclass: {} };
    } else if (model === 'masterclass') {
      next = { ...next, programType: 'masterclass', mentorship: {} };
    }
  }

  const name = groupName?.trim();
  if (name) {
    if (model === 'mentorship') {
      next = { ...next, mentorship: { ...(next.mentorship || {}), name } };
    } else if (model === 'masterclass') {
      next = { ...next, masterclass: { ...(next.masterclass || {}), title: name } };
    } else {
      // masterclass&mentorship / unknown → keep both per-type names aligned.
      next = {
        ...next,
        masterclass: { ...(next.masterclass || {}), title: name },
        mentorship: { ...(next.mentorship || {}), name },
      };
    }
  }
  return next;
}

export function ProgramCreationFlow() {
  const { state, dispatch } = useProgramCreation();
  // ATC: track active admin time across every creation sub-flow (AI / manual /
  // mentorship / guided). One mount here covers them all since they render in
  // this component's <Outlet/> and share state.groupId. The BE stops counting
  // once the program is "Invited". (The Program Design editor — AIMDv2Page — and
  // the structure editor — DesignView — carry their own mounts on their routes.)
  useAimdHeartbeat(state.groupId);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const reduxDispatch = useAppDispatch();
  const { location: dataLocation } = useProgramContext();
  const existingGroupName = useAppSelector((state) => state.groupDetails.data?.name);
  const existingGroupModel = useAppSelector((state) => state.groupDetails.data?.model);
  // Latest known model + name, so the async resume effect below can reconcile
  // the saved buildState even when the selector values change after it started.
  const groupModelRef = useRef<string>('');
  const groupNameRef = useRef<string>('');
  useEffect(() => {
    if (existingGroupModel) groupModelRef.current = existingGroupModel;
  }, [existingGroupModel]);
  useEffect(() => {
    if (existingGroupName) groupNameRef.current = existingGroupName;
  }, [existingGroupName]);

  // Adopt the existing program from the URL when the admin enters this
  // flow via the Learning Framework "Continue" link on a design-stage
  // program. Without this each sub-flow (manual / AI / mentorship /
  // guided) would treat the visit as a brand-new program and create a
  // second group on save. Runs for every /new-program/* route — the
  // structure-editor path (DesignView) doesn't mount this component, so
  // its own ManualBuilderInitializer remains the sole source of context
  // hydration there.
  const routeGroupId = params?.id && params.id !== 'admin' ? params.id : '';
  useEffect(() => {
    if (!routeGroupId) return;
    if (state.groupId === routeGroupId) return;
    // The context/localStorage still holds a draft from a DIFFERENT program
    // (the admin built one before and is now entering this one). Adopting the
    // new groupId without clearing would leak the old program's type, drafts,
    // modules and generationId into this one — and publish over the wrong
    // group. Reset first; the BE buildState hydration below repopulates the
    // correct data for this program when it exists.
    if (state.groupId) {
      dispatch({ type: 'RESET' });
    }
    dispatch({ type: 'SET_GROUP_ID', payload: routeGroupId });
  }, [routeGroupId, state.groupId, dispatch]);

  // Fetch the group doc once per routeGroupId — even when the context already
  // restored a matching groupId from localStorage (resuming the SAME program).
  // Without this, Redux groupDetails stays empty, so the name-mirror effect
  // below never fires and the Mentorship/MC/GM name field renders blank.
  // Mirrors AIMDv2Page's fetchedForGroupIdRef pattern.
  const fetchedForGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!routeGroupId) return;
    if (fetchedForGroupIdRef.current === routeGroupId) return;
    fetchedForGroupIdRef.current = routeGroupId;
    reduxDispatch(
      fetchGroupDetails({ grpId: routeGroupId, loc: dataLocation, useCache: false })
    );
  }, [routeGroupId, reduxDispatch, dataLocation]);

  // Resume-to-step: when the admin re-enters a design-stage program via
  // "Continue" they land on /start. Pull the persisted buildState from the
  // BE, re-hydrate the wizard context (programType, buildMode, ai content,
  // modules, generationId, …) and redirect to the sub-route matching how
  // far they got — so a paused AI course reopens on its Review step with
  // the generated content already populated. One-shot per group; only fires
  // from the start route so it never hijacks active in-flow navigation.
  const resumedForGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!routeGroupId) return undefined;
    if (resumedForGroupIdRef.current === routeGroupId) return undefined;
    const isOnStartStep = location.pathname.endsWith('/start') || location.pathname.endsWith('/new-program');
    if (!isOnStartStep) return undefined;

    resumedForGroupIdRef.current = routeGroupId;
    let cancelled = false;

    (async () => {
      let remote: any = null;
      // Hold the context's buildState save while we fetch: the state restored
      // from localStorage may be another admin's stale copy of this program.
      setBuildStateHydrating(routeGroupId, true);
      try {
        const resp = await API.getBuildState({ grpId: routeGroupId });
        remote = resp?.data ?? null;
      } catch (err) {
        console.error('Failed to load saved build state for resume', err);
      }
      if (cancelled) {
        setBuildStateHydrating(routeGroupId, false);
        return;
      }

      // The group's real model owns the masterclass/mentorship identity, so we
      // need it before deciding the resume route. It is normally already in
      // Redux (fetched by the effect above); fall back to a direct fetch if the
      // two requests raced and it isn't there yet.
      let groupModel = groupModelRef.current;
      let groupName = groupNameRef.current;
      if (!groupModel || !groupName) {
        try {
          const details = await reduxDispatch(
            fetchGroupDetails({ grpId: routeGroupId, loc: dataLocation, useCache: false })
          ).unwrap();
          groupModel = groupModel || (details as any)?.model || '';
          groupName = groupName || (details as any)?.name || '';
        } catch {
          /* leave empty → reconciliation is skipped */
        }
        if (cancelled) {
          setBuildStateHydrating(routeGroupId, false);
          return;
        }
      }

      // Drop a stale/cross-contaminated draft (wrong builder) and let the real
      // group name win over a leaked draft title.
      remote = reconcileBuildStateWithModel(remote, groupModel, groupName);

      if (remote && typeof remote === 'object' && remote.programType) {
        dispatch({ type: 'HYDRATE_FROM_BUILD_STATE', payload: remote });
      }
      setBuildStateHydrating(routeGroupId, false);

      // Prefer the freshly fetched BE state for routing; fall back to
      // whatever localStorage already restored into the context.
      const routeSource = remote?.programType ? remote : state;
      const target = resolveResumeRoute(routeSource);
      if (target) navigate(target, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeGroupId, location.pathname]);

  // Mirror the existing group's `name` into whichever per-type draft is
  // currently empty so the next step (mentorship Program Name input, AI
  // Purpose page, manual builder Program name) renders pre-filled.
  useEffect(() => {
    if (!routeGroupId || !existingGroupName) return;
    if (!state.masterclass?.title?.trim()) {
      dispatch({ type: 'UPDATE_MASTERCLASS', payload: { title: existingGroupName } });
    }
    if (!state.mentorship?.name?.trim()) {
      dispatch({ type: 'UPDATE_MENTORSHIP', payload: { name: existingGroupName } });
    }
  }, [routeGroupId, existingGroupName, state.masterclass?.title, state.mentorship?.name, dispatch]);

  useEffect(() => {
    // Se ainda não escolheu o tipo, força o usuário ficar no step "start"
    const isOnStartStep = location.pathname.endsWith('/start');

    if (!state.programType && !isOnStartStep) {
      navigate('start', { replace: true }); // <-- RELATIVO
    }
  }, [state.programType, location.pathname, navigate]);

  return <Outlet />;
}
